import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sun, Moon, LogOut, ChevronDown, User as UserIcon, Bell, Settings as SettingsIcon } from "lucide-react";
import { useTheme } from "@/theme/ThemeProvider";
import { useAuth } from "@/auth/AuthContext";
import { useFunder } from "@/funder/FunderContext";
import { EditProfileDialog } from "@/components/profile/EditProfileDialog";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";

function NotificationBell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [count, setCount] = useState(0);

  // Refetch on every route change so the badge mirrors auto-resolving alerts
  // (acting on a condition makes its notification — and this count — drop).
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const r = await api.get("/dashboard");
        if (active) setCount(r.data?.notifications?.length || 0);
      } catch {
        if (active) setCount(0);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [location.pathname]);

  return (
    <button
      type="button"
      data-testid="notification-bell"
      onClick={() => navigate("/")}
      aria-label={count ? `${count} active alert${count === 1 ? "" : "s"}` : "No active alerts"}
      className="relative grid h-9 w-9 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Bell className="h-[18px] w-[18px]" />
      {count > 0 && (
        <span
          data-testid="notification-bell-count"
          className="absolute -right-0.5 -top-0.5 grid h-4 min-w-[16px] place-items-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      type="button"
      data-testid="theme-toggle-button"
      onClick={toggleTheme}
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      className="grid h-9 w-9 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
    </button>
  );
}

function initials(name = "") {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const avatar = user?.avatar;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="user-menu-button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-md py-1 pl-1 pr-2 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="grid h-8 w-8 place-items-center overflow-hidden rounded-full bg-primary/12 text-[12px] font-semibold text-primary nums">
          {avatar ? (
            <img src={avatar} alt="avatar" className="h-8 w-8 object-cover" data-testid="user-menu-avatar" />
          ) : (
            initials(user?.name) || <UserIcon className="h-4 w-4" />
          )}
        </span>
        <span className="hidden text-sm font-medium text-foreground sm:block">{user?.name}</span>
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </button>

      {open && (
        <div
          data-testid="user-menu-dropdown"
          className="absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-lg border border-border bg-surface shadow-pop"
        >
          <div className="border-b border-border px-4 py-3">
            <p className="truncate text-sm font-semibold text-foreground" data-testid="user-menu-name">
              {user?.name}
            </p>
            <p className="truncate text-xs text-muted-foreground" data-testid="user-menu-email">
              {user?.email}
            </p>
            <span className="mt-2 inline-flex items-center rounded-full border border-border bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              {user?.role}
            </span>
          </div>
          <button
            type="button"
            data-testid="edit-profile-button"
            onClick={() => {
              setOpen(false);
              setEditing(true);
            }}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
          >
            <SettingsIcon className="h-4 w-4" />
            Edit profile
          </button>
          <button
            type="button"
            data-testid="logout-button"
            onClick={logout}
            className="flex w-full items-center gap-2 border-t border-border px-4 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      )}

      <EditProfileDialog open={editing} onOpenChange={setEditing} />
    </div>
  );
}

export function Topbar() {
  const { firmName } = useFunder();
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-surface/85 px-6 backdrop-blur-md md:px-8">
      <div className="flex items-center gap-3">
        <span className="font-serif text-lg text-foreground" data-testid="firm-name">
          {firmName || "MatterTab"}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <NotificationBell />
        <ThemeToggle />
        <span className="mx-1 hidden h-6 w-px bg-border sm:block" />
        <UserMenu />
      </div>
    </header>
  );
}
